import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import './DashboardView.css';
import HelpButton from '../components/HelpButton';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import { UserContext } from '../contexts/UserContext';

const DashboardView = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isGlobalAdmin = Boolean(user?.is_admin);

  const { getCurrentLanguage, getLocalizedName, getLocalizedFullName } = useLocale();
  const lang = getCurrentLanguage();

  // was "conferences"; now it's festschrifts, but structure is similar
  const [festschrifts, setFestschrifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Для дашборда хлебные крошки очищаем
  const resetBreadcrumbs = useCallback(() => {
    return [];
  }, []);
  useUpdateBreadcrumbs(() => resetBreadcrumbs());

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await api.get('/dashboard');
        let data = response.data;
        data = Array.isArray(data) ? data : [];
        setFestschrifts(data);
        setError('');
      } catch (err) {
        console.error(err);
        setError(
          lang === 'ru' ? 'Ошибка загрузки данных сборников' : 'Error loading festschrifts data',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [lang]);

  // Форматировать дату
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return d.toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US', options);
  };

  // --- Deadlines (display only) ---
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return d.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', options);
  };

  const renderDeadlineBadge = (deadline) => {
    if (!deadline || !deadline.due_at) return null;

    const dueText = formatDateTime(deadline.due_at);
    const label =
      deadline.role === 'author'
        ? lang === 'ru'
          ? 'Дедлайн автора'
          : 'Author deadline'
        : lang === 'ru'
          ? 'Дедлайн рецензента'
          : 'Reviewer deadline';

    const overdueText = deadline.overdue ? (lang === 'ru' ? ' (просрочено)' : ' (overdue)') : '';

    return (
      <span className={`deadline-badge ${deadline.overdue ? 'overdue' : ''}`}>
        {label}: {dueText}
        {overdueText}
      </span>
    );
  };

  // Переход на управление фестшрифтом
  const handleFestschriftManagement = (festschriftId, festschriftName) => {
    navigate(
      `/management/publishing/${festschriftId}?conferenceName=${encodeURIComponent(
        festschriftName,
      )}`,
    );
  };

  // Переход на страницу автора статьи (бывш. "тезис")
  const handleArticleClick = (
    festschriftId,
    festschriftName,
    sectionId,
    sectionName,
    articleId,
    articleTitle,
  ) => {
    navigate(
      `/author/articles/${articleId}?conferenceName=${encodeURIComponent(
        festschriftName,
      )}&sectionName=${sectionName}&thesisTitle=${encodeURIComponent(articleTitle)}`,
    );
  };

  // Переход на страницу ревью
  const handleReviewClick = (festschriftId, reviewId, festschriftName, articleTitle) => {
    navigate(
      `/publishing/${festschriftId}/review/${reviewId}?conferenceName=${encodeURIComponent(
        festschriftName,
      )}&thesisTitle=${encodeURIComponent(articleTitle)}&editable=True&flow=dashboard`,
    );
  };

  // Переход на задачу по ревью
  const handleTaskClick = (
    festschriftId,
    festschriftName,
    sectionId,
    sectionName,
    articleId,
    articleTitle,
  ) => {
    navigate(
      `/publishing/${festschriftId}/sections/${sectionId}/articles/${articleId}/task?conferenceName=${encodeURIComponent(
        festschriftName,
      )}&sectionName=${sectionName}&thesisTitle=${encodeURIComponent(articleTitle)}`,
    );
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        {lang === 'ru' ? 'Загрузка сборников...' : 'Loading festschrifts...'}
      </div>
    );
  }

  if (error) {
    return <div className="dashboard-error">{error}</div>;
  }

  // Отображение статуса ревью (по первой рецензии в списке)
  const reviewStatusRepresentation = (review) => {
    if (review == null || review.status === undefined) return '';
    const status = review.status;
    switch (status) {
      case 'pending':
        return lang === 'ru' ? 'Ожидание ответа автора' : 'Awaiting author response';
      case 'rejected':
        return lang === 'ru' ? 'Работа отклонена' : 'Submission rejected';
      case 'in_progress':
        return lang === 'ru' ? 'В процессе' : 'In progress';
      case 'approved':
        return lang === 'ru' ? 'Одобрено' : 'Approved';
      case 'revision_required':
        return lang === 'ru' ? 'Требуется доработка' : 'Revision required';
      case 'finalized':
        return lang === 'ru' ? 'Рецензирование завершено' : 'Review is finalized';
      default:
        return status;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-help-fab">
        <HelpButton helpId="dashboard.view.help" helpLabel="Подсказка" />
      </div>
      {isGlobalAdmin && (
        <div className="dashboard-admin-tools">
          <div className="dashboard-admin-tools-title">
            {lang === 'ru' ? 'Админ-инструменты' : 'Admin tools'}
          </div>
          <button
            className="dashboard-admin-tools-btn"
            onClick={() => navigate('/admin/help-tips')}
          >
            {lang === 'ru' ? 'Подсказки интерфейса' : 'UI help tips'}
          </button>
        </div>
      )}

      {!festschrifts || festschrifts.length === 0 ? (
        <div className="dashboard-empty">
          {lang === 'ru'
            ? 'Сейчас у вас нет работ для загрузки или оценки.'
            : 'You currently have no submissions to upload or review.'}
        </div>
      ) : (
        festschrifts.map((fest) => (
          <div key={fest.id} className="conference-block">
            <div className="conference-header">
              <div className="conference-info">
                <h2
                  className="conference-title clickable"
                  onClick={() => navigate(`/publishing/${fest.slug || fest.id}/info`)}
                >
                  {getLocalizedName(fest.title)}
                </h2>
                <p className="conference-dates">
                  {lang === 'ru' ? 'Начало:' : 'Start:'} {formatDate(fest.start_date)} |{' '}
                  {lang === 'ru' ? 'Окончание:' : 'End:'} {formatDate(fest.end_date)}
                </p>
              </div>
              {fest.is_admin && (
                <button
                  className="manage-btn"
                  onClick={() =>
                    handleFestschriftManagement(fest.slug || fest.id, getLocalizedName(fest.title))
                  }
                >
                  {lang === 'ru' ? 'Управление' : 'Manage'}
                </button>
              )}
            </div>

            {fest.sections &&
              fest.sections.map((sec) => (
                <div key={sec.id} className="section-block">
                  <h3 className="section-title">{getLocalizedName(sec.title)}</h3>

                  {/* Блок голосования по статьям */}
                  {sec.election_open && (
                    <div className="election-block">
                      <h4 className="election-title">
                        {lang === 'ru' ? 'Голосование по статьям' : 'Article voting'}
                      </h4>
                      <p className="election-date">
                        {lang === 'ru' ? 'Голосование до: ' : 'Election until: '}
                        {formatDate(sec.election_till_date)}
                      </p>
                      <button
                        className="election-btn"
                        onClick={() =>
                          navigate(
                            `/publishing/${fest.slug || fest.id}/sections/${
                              sec.slug || sec.id
                            }/election?conferenceName=${encodeURIComponent(
                              getLocalizedName(fest.title),
                            )}&sectionName=${encodeURIComponent(getLocalizedName(sec.title))}`,
                          )
                        }
                      >
                        {sec.election_results_present
                          ? lang === 'ru'
                            ? 'Редактировать анкету'
                            : 'Edit questionnaire'
                          : lang === 'ru'
                            ? 'Заполнить анкету'
                            : 'Fill questionnaire'}
                      </button>
                    </div>
                  )}

                  {/* Мои статьи (как автор) */}
                  {sec.articles && sec.articles.length > 0 && (
                    <div className="sub-section">
                      <h4 className="sub-section-title">
                        {lang === 'ru' ? 'Мои статьи' : 'My Articles'}
                      </h4>
                      <ul className="item-list">
                        {sec.articles.map((article) => (
                          <li
                            key={article.id}
                            onClick={() =>
                              handleArticleClick(
                                fest.id,
                                getLocalizedName(fest.title),
                                sec.id,
                                getLocalizedName(sec.title),
                                article.id,
                                getLocalizedName(article.title),
                              )
                            }
                          >
                            <div className="item-line">
                              <span className="item-thesis-title">
                                {getLocalizedName(article.title)}
                              </span>
                            </div>
                            {renderDeadlineBadge(article.deadline)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Мои ревью (принятые задачи) */}
                  {sec.reviews && sec.reviews.length > 0 && (
                    <div className="sub-section">
                      <h4 className="sub-section-title">
                        {lang === 'ru' ? 'Мои ревью' : 'My Reviews'}
                      </h4>
                      <ul className="item-list">
                        {sec.reviews.map((reviewTask) => {
                          const firstReview = reviewTask.reviews && reviewTask.reviews[0];
                          const reviewId = firstReview && firstReview.id;
                          const statusText = firstReview && reviewStatusRepresentation(firstReview);

                          return (
                            <li
                              key={reviewTask.article_id}
                              onClick={() =>
                                reviewId &&
                                handleReviewClick(
                                  fest.id,
                                  reviewId,
                                  getLocalizedName(fest.title),
                                  getLocalizedName(reviewTask.article_title),
                                )
                              }
                            >
                              <div className="item-line">
                                <span className="item-thesis-title">
                                  {getLocalizedName(reviewTask.article_title)}
                                </span>
                                <span className="item-author">
                                  {getLocalizedFullName(reviewTask.article_author_full_name)}
                                </span>{' '}
                                (<span className="item-status">{statusText || ''}</span>)
                              </div>

                              {firstReview && renderDeadlineBadge(firstReview.deadline)}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Назначенные задачи (pending) */}
                  {sec.pending_tasks && sec.pending_tasks.length > 0 && (
                    <div className="sub-section">
                      <h4 className="sub-section-title">
                        {lang === 'ru' ? 'Назначенные задачи' : 'Assigned Tasks'}
                      </h4>
                      <ul className="item-list">
                        {sec.pending_tasks.map((task) => (
                          <li
                            key={task.article_id}
                            onClick={() =>
                              handleTaskClick(
                                fest.slug || fest.id,
                                getLocalizedName(fest.title),
                                sec.slug || sec.id,
                                getLocalizedName(sec.title),
                                task.article_id,
                                getLocalizedName(task.article_title),
                              )
                            }
                          >
                            <span className="item-thesis-title">
                              {getLocalizedName(task.article_title)}
                            </span>{' '}
                            –{' '}
                            <span className="item-author">
                              {getLocalizedFullName(task.article_author_full_name)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Отклонённые задачи */}
                  {sec.rejected_tasks && sec.rejected_tasks.length > 0 && (
                    <div className="sub-section">
                      <h4 className="sub-section-title">
                        {lang === 'ru' ? 'Отклоненные задачи' : 'Rejected Tasks'}
                      </h4>
                      <ul className="item-list">
                        {sec.rejected_tasks.map((task) => (
                          <li key={task.article_id}>
                            <span className="item-thesis-title">
                              {getLocalizedName(task.article_title)}
                            </span>{' '}
                            –{' '}
                            <span className="item-author">
                              {getLocalizedFullName(task.article_author_full_name)}
                            </span>{' '}
                            – <span>{task.reject_reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
          </div>
        ))
      )}
    </div>
  );
};

export default DashboardView;

